import React, { useEffect } from 'react'
import { useSelector, useDispatch } from 'react-redux'
import { Helmet } from 'react-helmet-async'

import { checkAuthStatus } from './store/slices/authSlice'
import LoadingSpinner from './components/common/LoadingSpinner'
import ErrorBoundary from './components/common/ErrorBoundary'
import { getAppType } from './utils/subdomainDetector'

// Import app components
import PublicApp from './apps/PublicApp'
import TenantApp from './apps/TenantApp'
import PlatformAdminApp from './apps/PlatformAdminApp'

function App() {
  const dispatch = useDispatch()
  const { isAuthenticated, user, token } = useSelector((state) => state.auth)
  const [isRehydrated, setIsRehydrated] = React.useState(false)
  const [authChecked, setAuthChecked] = React.useState(false)
  const [appType] = React.useState(() => {
    // Detect app type once on mount
    const detectedAppType = getAppType()
    return detectedAppType
  })
  useEffect(() => {
    // Wait for redux-persist to rehydrate
    const timer = setTimeout(() => {
      setIsRehydrated(true)
    }, 100)

    return () => clearTimeout(timer)
  }, [])

  useEffect(() => {
    // Check auth status after rehydration (only if we have a token)
    if (isRehydrated && !authChecked) {
      if (token) {
        dispatch(checkAuthStatus()).finally(() => {
          setAuthChecked(true)
        })
      } else {
        setAuthChecked(true)
      }
    }
  }, [isRehydrated, authChecked, token, dispatch])

  // Show loading while rehydrating
  if (!isRehydrated) {
    return <LoadingSpinner />
  }

  // Determine which app to show based on authentication and app type
  const shouldShowTenantApp = () => {
    // Never show tenant app for platform admins
    if (isAuthenticated && user) {
      const isPlatformAdmin = user.is_platform_admin || user.isPlatformAdmin
      if (isPlatformAdmin) {
        return false
      }
    }

    // If on PUBLIC subdomain but user is authenticated (and not platform admin)
    if (appType === 'PUBLIC' && isAuthenticated && user) {
      return true
    }
    return appType === 'TENANT'
  }

  const shouldShowPlatformAdminApp = () => {
    // Show platform admin app if:
    // 1. On admin subdomain AND user is platform admin, OR
    // 2. User is platform admin (even on localhost)

    if (appType === 'PLATFORM_ADMIN') {
      // If on admin subdomain, check if user is actually platform admin
      if (isAuthenticated && user) {
        const isPlatformAdmin = user.is_platform_admin || user.isPlatformAdmin
        return isPlatformAdmin
      }
      // Not authenticated yet, show platform admin app (will show login)
      return true
    }

    if (isAuthenticated && user) {
      const isPlatformAdmin = user.is_platform_admin || user.isPlatformAdmin
      if (isPlatformAdmin) {
        return true
      }
    }

    return false
  }

  const shouldShowPublicApp = () => {
    // Show public app only if not authenticated or on public subdomain without auth
    return appType === 'PUBLIC' && !isAuthenticated
  }

  // For authenticated users, wait for auth check
  if (isAuthenticated && !authChecked) {
    return <LoadingSpinner />
  }

  // Render appropriate app
  if (shouldShowPublicApp()) {
    return (
      <ErrorBoundary>
        <Helmet>
          <title>EDUNA Platform</title>
          <meta name="description" content="Educational and Professional Course Management System" />
        </Helmet>
        <PublicApp />
      </ErrorBoundary>
    )
  }

  // For tenant and platform admin apps, wait for auth check
  if (!authChecked) {
    return <LoadingSpinner />
  }

  return (
    <ErrorBoundary>
      <Helmet>
        <title>EDUNA Platform</title>
        <meta name="description" content="Educational and Professional Course Management System" />
      </Helmet>
      {shouldShowPlatformAdminApp() && <PlatformAdminApp />}
      {shouldShowTenantApp() && <TenantApp />}
    </ErrorBoundary>
  )
}

export default App